import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { DestinationGridCard } from '@/components/ui/destination-grid-card';
import { Seo } from '@/components/Seo';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { X, Loader2 } from 'lucide-react';
import { useDestinations, UiDestination, SkillLevel } from '@/hooks/useDestinations';
import { getFriendlyErrorMessage } from '@/lib/errors';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WAVE_TYPES = ['beach', 'reef', 'point'];
const SKILL_LEVELS = ['beginner', 'intermediate', 'advanced', 'expert'];

const Destinations = () => {
  const navigate = useNavigate();
  const { data: destinations = [], isLoading, error } = useDestinations();

  const [filters, setFilters] = useState({
    continent: '',
    country: '',
    waveType: '',
    skillLevel: '',
    month: '',
  });

  const countries = useMemo(
    () => [...new Set(destinations.map((d) => d.country).filter((c) => c && c !== '—'))].sort(),
    [destinations]
  );
  const continents = useMemo(
    () => [...new Set(destinations.map((d) => d.continent).filter((c) => c && c !== '—'))].sort(),
    [destinations]
  );

  const filteredDestinations: UiDestination[] = useMemo(() => {
    return destinations.filter((dest) => {
      if (filters.continent && dest.continent !== filters.continent) return false;
      if (filters.country && dest.country !== filters.country) return false;
      if (filters.waveType && dest.waveType !== filters.waveType) return false;
      if (filters.skillLevel) {
        const levels = dest.skillLevels;
        const match = levels.length ? levels.includes(filters.skillLevel as SkillLevel) : dest.skillLevel === filters.skillLevel;
        if (!match) return false;
      }
      if (filters.month && !dest.bestMonths.includes(filters.month)) return false;
      return true;
    });
  }, [destinations, filters]);

  const clearFilters = () => {
    setFilters({ continent: '', country: '', waveType: '', skillLevel: '', month: '' });
  };

  const hasActiveFilters = Object.values(filters).some(Boolean);

  return (
    <Layout>
      <Seo
        title="Surf Destinations | Compare Seasons, Waves, Costs and Crowds"
        description="Explore surf destinations around the world and compare surf seasons, ability levels, waves, crowds, costs and travel experience."
        path="/destinations"
      />
      {/* Hero Section - Compact */}
      <section className="relative h-[20vh] md:h-[25vh] min-h-[140px] md:min-h-[200px] flex items-end justify-center">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1502680390469-be75c86b636f?w=1920&q=80"
            alt="Surf destinations"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
        </div>
        <div className="container mx-auto px-4 pb-6 relative z-10 text-center">
          <h1 className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold text-foreground mb-2">
            Surf Destinations
          </h1>
          <p className="text-xs sm:text-sm md:text-base text-foreground/80 max-w-xl mx-auto">
            World-class surf spots curated by surfers, for surfers.
          </p>
        </div>
      </section>

      {/* Compact Filters Section */}
      <section className="relative -mt-4">
        <div className="container mx-auto px-4">
          <div className="bg-card rounded-lg md:rounded-2xl border border-foreground/20 p-2 md:p-4 lg:p-6 shadow-xl">
            {/* Mobile: Stacked filters */}
            <div className="flex flex-col gap-1.5 md:hidden">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Filter:</span>
                {hasActiveFilters && (
                  <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-1 h-7 px-2 text-xs">
                    <X className="h-3 w-3" />
                    Clear
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-3 gap-1.5">
                <Select value={filters.continent} onValueChange={(v) => setFilters((f) => ({ ...f, continent: v }))}>
                  <SelectTrigger aria-label="Filter by continent" className="bg-background border-foreground/25 h-7 text-[10px]">
                    <SelectValue placeholder="Continent" />
                  </SelectTrigger>
                  <SelectContent>
                    {continents.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={filters.country} onValueChange={(v) => setFilters((f) => ({ ...f, country: v }))}>
                  <SelectTrigger aria-label="Filter by country" className="bg-background border-foreground/25 h-7 text-[10px]">
                    <SelectValue placeholder="Country" />
                  </SelectTrigger>
                  <SelectContent>
                    {countries.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={filters.waveType} onValueChange={(v) => setFilters((f) => ({ ...f, waveType: v }))}>
                  <SelectTrigger aria-label="Filter by wave type" className="bg-background border-foreground/25 h-7 text-[10px]">
                    <SelectValue placeholder="Wave" />
                  </SelectTrigger>
                  <SelectContent>
                    {WAVE_TYPES.map((w) => (
                      <SelectItem key={w} value={w} className="capitalize">{w}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={filters.skillLevel} onValueChange={(v) => setFilters((f) => ({ ...f, skillLevel: v }))}>
                  <SelectTrigger aria-label="Filter by skill level" className="bg-background border-foreground/25 h-7 text-[10px]">
                    <SelectValue placeholder="Skill" />
                  </SelectTrigger>
                  <SelectContent>
                    {SKILL_LEVELS.map((s) => (
                      <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={filters.month} onValueChange={(v) => setFilters((f) => ({ ...f, month: v }))}>
                  <SelectTrigger aria-label="Filter by month" className="bg-background border-foreground/25 h-7 text-[10px]">
                    <SelectValue placeholder="Month" />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((m) => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {hasActiveFilters ? (
                  <Button variant="ghost" size="sm" onClick={clearFilters} className="h-7 px-2 text-[10px]">
                    <X className="h-2.5 w-2.5" />
                  </Button>
                ) : (
                  <div className="flex items-center justify-center text-[10px] text-muted-foreground">
                    {filteredDestinations.length}/{destinations.length}
                  </div>
                )}
              </div>
            </div>

            {/* Desktop: Inline filters */}
            <div className="hidden md:flex flex-wrap gap-3 items-center">
              <span className="text-sm text-muted-foreground">Filter:</span>

              <Select value={filters.continent} onValueChange={(v) => setFilters((f) => ({ ...f, continent: v }))}>
                <SelectTrigger aria-label="Filter by continent" className="bg-background border-foreground/25 w-[140px] h-9 text-sm">
                  <SelectValue placeholder="Continent" />
                </SelectTrigger>
                <SelectContent>
                  {continents.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filters.country} onValueChange={(v) => setFilters((f) => ({ ...f, country: v }))}>
                <SelectTrigger aria-label="Filter by country" className="bg-background border-foreground/25 w-[130px] h-9 text-sm">
                  <SelectValue placeholder="Country" />
                </SelectTrigger>
                <SelectContent>
                  {countries.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filters.waveType} onValueChange={(v) => setFilters((f) => ({ ...f, waveType: v }))}>
                <SelectTrigger aria-label="Filter by wave type" className="bg-background border-foreground/25 w-[120px] h-9 text-sm">
                  <SelectValue placeholder="Wave Type" />
                </SelectTrigger>
                <SelectContent>
                  {WAVE_TYPES.map((w) => (
                    <SelectItem key={w} value={w} className="capitalize">{w}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filters.skillLevel} onValueChange={(v) => setFilters((f) => ({ ...f, skillLevel: v }))}>
                <SelectTrigger aria-label="Filter by skill level" className="bg-background border-foreground/25 w-[130px] h-9 text-sm">
                  <SelectValue placeholder="Skill Level" />
                </SelectTrigger>
                <SelectContent>
                  {SKILL_LEVELS.map((s) => (
                    <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filters.month} onValueChange={(v) => setFilters((f) => ({ ...f, month: v }))}>
                <SelectTrigger aria-label="Filter by month" className="bg-background border-foreground/25 w-[110px] h-9 text-sm">
                  <SelectValue placeholder="Month" />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS.map((m) => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-1 h-9 px-2">
                  <X className="h-3 w-3" />
                  Clear
                </Button>
              )}

              <span className="ml-auto text-xs text-muted-foreground">
                {filteredDestinations.length} of {destinations.length}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-2 md:py-12 lg:py-16">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Loading destinations…</p>
          </div>
        ) : error ? (
          <div className="text-center py-12 md:py-20">
            {/* Default heading color, not text-destructive: matches the
                DestinationDetail.tsx "Destination Not Found" error heading —
                text-destructive here measured 3.63:1 against the
                page background, well under WCAG AA's 4.5:1. The wording
                itself ("Couldn't load") still reads as an error state. */}
            <p className="text-foreground mb-2 text-base">Couldn't load destinations</p>
            <p className="text-muted-foreground text-sm">{getFriendlyErrorMessage(error)}</p>
          </div>
        ) : filteredDestinations.length === 0 ? (
          <div className="text-center py-12 md:py-20">
            <p className="text-muted-foreground mb-4 text-base md:text-lg">No destinations match your filters</p>
            <Button variant="outline" onClick={clearFilters} className="rounded-full">Clear Filters</Button>
          </div>
        ) : (
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-3 lg:gap-6 xl:grid-cols-4">
              {filteredDestinations.map((dest) => (
                <DestinationGridCard
                  key={dest.id}
                  destination={dest}
                  onClick={() => navigate(`/destinations/${dest.id}`)}
                />
              ))}
            </div>
          </div>
        )}
      </section>
      <div className="h-8 md:h-12" />
    </Layout>
  );
};

export default Destinations;
